import React, { useState } from 'react';
import {
  Box, Typography, Button, Collapse, Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const C = {
  navy:      '#273F77',
  orange:    '#E79A2B',
  bg:        '#f5f6fa',
  cardBg:    '#ffffff',
  border:    '#e8eaf0',
  textMuted: '#8492a6',
  green:     '#2e7d32',
  red:       '#c62828',
};

// ─── SVG score ring ───────────────────────────────────────────────────────────
const ScoreRing = ({ percentage, passed }) => {
  const size = 150;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(percentage, 100) / 100) * circ;
  const color = passed ? C.green : C.red;

  return (
    <Box position="relative" display="inline-flex" alignItems="center" justifyContent="center">
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dashoffset 0.9s ease' }}
        />
      </svg>
      <Box position="absolute" textAlign="center">
        <Typography variant="h4" fontWeight={900} color={color} lineHeight={1}>
          {Math.round(percentage)}%
        </Typography>
        <Typography variant="caption" color={C.textMuted} fontWeight={700}
          textTransform="uppercase" letterSpacing={0.5}>
          {passed ? 'Passed' : 'Failed'}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Question review card ─────────────────────────────────────────────────────
const QuestionReview = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  const isCorrect = item.is_correct;

  const getOptions = () => {
    if (item.type === 'True/False') return { True: 'True', False: 'False' };
    return item.options || {};
  };

  return (
    <Paper elevation={0} sx={{
      borderRadius: '14px', mb: 1.5, overflow: 'hidden',
      border: `1.5px solid ${isCorrect ? 'rgba(46,125,50,0.3)' : 'rgba(198,40,40,0.3)'}`,
    }}>
      {/* Header */}
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
          cursor: 'pointer', userSelect: 'none',
          backgroundColor: isCorrect ? 'rgba(46,125,50,0.04)' : 'rgba(198,40,40,0.04)',
        }}
      >
        {isCorrect
          ? <CheckCircleIcon sx={{ color: C.green, fontSize: 20, flexShrink: 0 }} />
          : <CancelIcon      sx={{ color: C.red,   fontSize: 20, flexShrink: 0 }} />
        }
        <Typography variant="body2" fontWeight={600} flex={1} color="text.primary">
          Q{index + 1}. {item.question}
        </Typography>
        {/* Type pill */}
        <Box sx={{
          px: 1, py: 0.25, borderRadius: '20px', flexShrink: 0,
          backgroundColor: item.type === 'MCQ' ? 'rgba(33,150,243,0.1)'
            : item.type === 'True/False' ? 'rgba(76,175,80,0.1)' : 'rgba(255,152,0,0.1)',
        }}>
          <Typography variant="caption" fontWeight={700}
            color={item.type === 'MCQ' ? '#1565c0' : item.type === 'True/False' ? C.green : '#e65100'}>
            {item.type}
          </Typography>
        </Box>
        <ExpandMoreIcon sx={{
          color: C.textMuted, flexShrink: 0,
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }} />
      </Box>

      {/* Options */}
      <Collapse in={open}>
        <Box sx={{ height: 1, backgroundColor: C.border }} />
        <Box p={2.5}>
          {Object.entries(getOptions()).map(([key, val]) => {
            const isUser    = item.user_answer === key;
            const isCorrectA = item.correct_answer === key;
            let borderColor = C.border, bg = 'transparent', dotColor = C.textMuted;

            if (isCorrectA)          { borderColor = C.green; bg = 'rgba(46,125,50,0.07)'; dotColor = C.green; }
            if (isUser && !isCorrectA) { borderColor = C.red;   bg = 'rgba(198,40,40,0.07)'; dotColor = C.red; }

            return (
              <Box key={key} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                border: `1.5px solid ${borderColor}`, borderRadius: '10px',
                px: 2, py: 1, mb: 0.75, backgroundColor: bg,
              }}>
                <Typography variant="caption" fontWeight={800} minWidth={16} color={dotColor}>
                  {key}
                </Typography>
                <Typography variant="body2" flex={1} fontWeight={isCorrectA ? 600 : 400}>
                  {val}
                </Typography>
                {isCorrectA && <CheckCircleIcon sx={{ fontSize: 15, color: C.green }} />}
                {isUser && !isCorrectA && <CancelIcon sx={{ fontSize: 15, color: C.red }} />}
              </Box>
            );
          })}

          {/* Summary */}
          <Box mt={1.5} display="flex" gap={3} pt={1.5}
            sx={{ borderTop: `1px solid ${C.border}` }}>
            <Box>
              <Typography variant="caption" color={C.textMuted} fontWeight={600}
                textTransform="uppercase" letterSpacing={0.4} display="block" mb={0.25}>
                Your answer
              </Typography>
              <Typography variant="body2" fontWeight={700}
                color={isCorrect ? C.green : C.red}>
                {item.user_answer || '—'}
              </Typography>
            </Box>
            {!isCorrect && (
              <Box>
                <Typography variant="caption" color={C.textMuted} fontWeight={600}
                  textTransform="uppercase" letterSpacing={0.4} display="block" mb={0.25}>
                  Correct answer
                </Typography>
                <Typography variant="body2" fontWeight={700} color={C.green}>
                  {item.correct_answer}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const QuizResult = ({ result, onClose, onRetry, canRetry }) => {
  const [showReview, setShowReview] = useState(false);
  const passed     = result.passed;
  const wrongCount = (result.total_questions || 0) - (result.correct_answers || 0);

  return (
    <Box maxWidth={720} mx="auto">

      {/* Score header */}
      <Box textAlign="center" py={4}>
        <ScoreRing percentage={result.percentage} passed={passed} />
        <Typography variant="h4" fontWeight={900}
          color={passed ? C.green : C.red} mt={2.5} mb={1}>
          {passed ? 'You Passed! 🎉' : 'Better Luck Next Time'}
        </Typography>
        <Typography variant="body1" color={C.textMuted} maxWidth={420} mx="auto">
          {passed
            ? 'Great work! You\'ve successfully completed this quiz.'
            : `You needed ${result.passing_score}% to pass. Review your answers below.`}
        </Typography>
      </Box>

      {/* Stats */}
      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={4}>
        {[
          { label: 'Score',         value: `${result.percentage}%`, color: passed ? C.green : C.red },
          { label: 'Correct',       value: result.correct_answers,  color: C.green },
          { label: 'Wrong',         value: wrongCount,              color: wrongCount > 0 ? C.red : 'text.primary' },
          { label: 'Attempts Left', value: result.remaining_attempts, color: C.navy },
        ].map(({ label, value, color }) => (
          <Paper key={label} elevation={0} sx={{
            borderRadius: '16px', border: `1px solid ${C.border}`, p: 2, textAlign: 'center',
          }}>
            <Typography variant="h4" fontWeight={900} color={color} lineHeight={1.1}>
              {value}
            </Typography>
            <Typography variant="caption" color={C.textMuted} fontWeight={600}
              mt={0.5} display="block" textTransform="uppercase" letterSpacing={0.4}>
              {label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Review toggle */}
      {result.question_results?.length > 0 && (
        <Box mb={4}>
          <Box
            onClick={() => setShowReview((v) => !v)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', p: 2, borderRadius: '14px', mb: showReview ? 1.5 : 0,
              border: `1.5px solid ${C.navy}`,
              backgroundColor: 'rgba(39,63,119,0.04)',
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="subtitle1" fontWeight={700} color={C.navy}>
                Review Answers
              </Typography>
              {wrongCount > 0 && (
                <Box sx={{
                  px: 1, py: 0.25, borderRadius: '20px',
                  backgroundColor: 'rgba(198,40,40,0.1)',
                }}>
                  <Typography variant="caption" fontWeight={700} color={C.red}>
                    {wrongCount} incorrect
                  </Typography>
                </Box>
              )}
              <Box sx={{
                px: 1, py: 0.25, borderRadius: '20px',
                backgroundColor: 'rgba(46,125,50,0.1)',
              }}>
                <Typography variant="caption" fontWeight={700} color={C.green}>
                  {result.correct_answers} correct
                </Typography>
              </Box>
            </Box>
            <ExpandMoreIcon sx={{
              color: C.navy, transition: 'transform 0.2s',
              transform: showReview ? 'rotate(180deg)' : 'rotate(0deg)',
            }} />
          </Box>

          <Collapse in={showReview}>
            {result.question_results.map((item, i) => (
              <QuestionReview key={item.question_id} item={item} index={i} />
            ))}
          </Collapse>
        </Box>
      )}

      <Box sx={{ height: 1, backgroundColor: C.border, mb: 3 }} />

      {/* Actions */}
      <Box display="flex" gap={2} pb={2}>
        <Button
          fullWidth variant="outlined" size="large"
          onClick={onClose}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: C.navy, color: C.navy,
            textTransform: 'none', borderRadius: '12px', fontWeight: 600,
            '&:hover': { backgroundColor: 'rgba(39,63,119,0.04)' },
          }}
        >
          Back to Modules
        </Button>
        {canRetry && !passed && (
          <Button
            fullWidth variant="contained" size="large"
            onClick={onRetry}
            startIcon={<ReplayIcon />}
            sx={{
              backgroundColor: C.orange, color: '#fff',
              textTransform: 'none', borderRadius: '12px', fontWeight: 700,
              boxShadow: '0 4px 14px rgba(231,154,43,0.4)',
              '&:hover': { backgroundColor: '#d4891f' },
            }}
          >
            Try Again
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default QuizResult;